import * as THREE from 'three';
import { CORRIDOR_CEILING_Y } from '../../game/corridor';
import { createGridMaterial } from './createGridMaterial';

export interface ArcadeSceneBoot {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  canvas: HTMLCanvasElement;
  mouse: THREE.Vector2;
  cameraCenter: THREE.Vector3;
  cameraHorzLimit: number;
  cameraVertLimit: number;
  idleCube: THREE.Mesh;
  gridFloor: THREE.Mesh;
  gridCeiling: THREE.Mesh;
  gridMaterialFloor: THREE.ShaderMaterial;
  gridMaterialCeiling: THREE.ShaderMaterial;
  clock: THREE.Clock;
  updateIdleCamera: () => void;
  onDocumentMouseMove: (event: MouseEvent) => void;
}

/**
 * Build the Three.js desktop/arcade scene (canvas, camera, grids, idle cube).
 * Pure boot helper — no React, no ArcadeGame.
 */
export function createArcadeScene(): ArcadeSceneBoot {
  const scene = new THREE.Scene();
  const cameraCenter = new THREE.Vector3();
  const cameraHorzLimit = 3;
  const cameraVertLimit = 3;
  const mouse = new THREE.Vector2();
  scene.background = new THREE.Color(0x23272a);

  // Far plane stays large so the corridor/horizon remain visible during long arcade runs.
  const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.5, 4000);
  camera.position.set(0, 20, 200);
  camera.lookAt(scene.position);

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(window.innerWidth, window.innerHeight);
  // Fixed full-viewport layer so the canvas never stacks below another canvas in layout flow
  const canvas = renderer.domElement;
  canvas.className = 'arcade-scene-canvas';
  canvas.style.position = 'fixed';
  canvas.style.inset = '0';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  // Behind #root (z-index: 1) so desktop chrome stays clickable
  canvas.style.zIndex = '0';
  canvas.style.display = 'block';
  canvas.style.pointerEvents = 'none';
  document.body.appendChild(canvas);

  // Infinite grids: shader places verts around the camera, so disable frustum
  // culling (bounding box stays at the origin and would cull once you fly away).
  const hex = 0xf2f2f2;
  const color = new THREE.Color(hex);
  const size = 8;
  const distance = 350;

  const gridgeometry = new THREE.PlaneBufferGeometry(2, 2, 1, 1);
  const gridMaterialFloor = createGridMaterial(size, color, distance);
  const gridMaterialCeiling = createGridMaterial(size, color, distance);

  const gridFloor = new THREE.Mesh(gridgeometry, gridMaterialFloor);
  const gridCeiling = new THREE.Mesh(gridgeometry, gridMaterialCeiling);
  // Shader displaces verts to the camera; never cull based on the tiny mesh AABB.
  gridFloor.frustumCulled = false;
  gridCeiling.frustumCulled = false;
  gridCeiling.position.set(0, CORRIDOR_CEILING_Y, 0);

  scene.add(gridFloor);
  scene.add(gridCeiling);

  // Idle home cube (also used as “press space” start affordance)
  const idleCube = new THREE.Mesh(new THREE.BoxGeometry(12, 12, 12), [
    new THREE.MeshBasicMaterial({ color: 0x80bcbd }),
    new THREE.MeshBasicMaterial({ color: 0xaad9bb }),
    new THREE.MeshBasicMaterial({ color: 0xd5f0c1 }),
    new THREE.MeshBasicMaterial({ color: 0xf9f7c9 }),
    new THREE.MeshBasicMaterial({ color: 0xd9edbf }),
    new THREE.MeshBasicMaterial({ color: 0xdcffb7 }),
  ]);
  scene.add(idleCube);
  idleCube.position.set(0, 15, 125);

  const clock = new THREE.Clock();

  function updateIdleCamera() {
    camera.position.x = cameraCenter.x + cameraHorzLimit * mouse.x;
    camera.position.y = cameraCenter.y + 15 + cameraVertLimit * mouse.y;
    camera.lookAt(scene.position);
  }

  function onDocumentMouseMove(event: MouseEvent) {
    event.preventDefault();
    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  }

  return {
    scene,
    camera,
    renderer,
    canvas,
    mouse,
    cameraCenter,
    cameraHorzLimit,
    cameraVertLimit,
    idleCube,
    gridFloor,
    gridCeiling,
    gridMaterialFloor,
    gridMaterialCeiling,
    clock,
    updateIdleCamera,
    onDocumentMouseMove,
  };
}
